<script setup>
import { ref, computed, watch } from "vue";
import { router, useForm, usePage } from "@inertiajs/vue2";
import { useVuetify } from "../../Composables/useVuetify.js";
import axios from "axios";

import GetStartedLayout from "../../Layouts/GetStartedLayout.vue";

// Composables
const breakpoint = useVuetify().breakpoint;

// Props
const props = defineProps({
    errors: {
        type: Object,
        default: () => ({}),
    },
    client: {
        type: Object,
        default: () => ({}),
    },
    child: {
        type: Object,
        default: () => ({}),
    },
    review: {
        type: Object,
        default: () => ({}),
    },
    shoppingCartItems: {
        type: Number,
        default: 0,
    },
});

console.log(props.client);

// Data

const layout = ref(null);
const loading = ref(false);
const classes = ref([]);
const showFullDescription = ref(false);

const description = `Our musical adventure for toddlers and their caregivers. At each 40 minute class, we’ll sing songs, move our bodies, dance to different styles of music, read sing-along books, and play lots of musical instruments while your child reinforces their naturally-developing senses of rhythm, melody, rhyme, speech, and movement.<br><br> There are no prerequisites, the class is designed for you and your little one to jump right in and participate. Register for your free first class!`;
const breadcrumbs = computed(() => {
    if (props.client.loginable) return ["Home", "Musicologie Junior"];
    return ["Get Started", "Programs", "Musicologie Junior"];
});

const form = useForm({});

// Computed

const tinyScreen = computed(() => {
    return breakpoint.width < 350;
});

// Methods

function getClasses() {
    loading.value = true;
    axios
        .get("/api/lessons/upcoming-group-events", {
            params: {
                title: "Musicologie Junior",
                location:
                    props.client.location?.id ??
                    props.client.selected_location?.id,
            },
        })
        .then((resp) => {
            classes.value = resp.data;
            loading.value = false;
        })
        .catch((error) => {
            loading.value = false;
            console.error("Error fetching classes:", error);
        });
}

getClasses();
</script>

<template>
    <GetStartedLayout
        ref="layout"
        :errors="errors"
        noContainer
        fluid
        :breadcrumbs="breadcrumbs"
        :guest="client"
        :child="child"
        :review="review"
        :hideNavOnScroll="breakpoint.xs"
        :location="client.location"
    >
        <template #toggleFilter>
            <v-btn
                v-if="shoppingCartItems"
                icon
                outlined
                color="white"
                @click="$inertia.get(route('checkout.show'))"
                ><v-badge
                    :content="shoppingCartItems"
                    color="secondary"
                    overlap
                    bordered
                >
                    <v-icon class="white--text">shopping_cart</v-icon></v-badge
                ></v-btn
            >
        </template>
        <v-col
            :class="{ 'mt-4': tinyScreen }"
            class="white--text px-0 mx-auto"
            cols="12"
            sm="11"
            md="10"
            lg="8"
            xl="6"
        >
            <v-card
                class="mx-auto"
                :tile="breakpoint.xs"
                :class="{
                    'soli-round': breakpoint.smAndUp,
                    'py-4': breakpoint.xs,
                }"
            >
                <v-container fluid>
                    <div
                        :class="{ 'grid-cols-2 gap-4': breakpoint.mdAndUp }"
                        class="grid grid-cols-1"
                    >
                        <div class="flex flex-col">
                            <div
                                :class="{
                                    ' pt-4': breakpoint.smAndUp,
                                    'px-4': !tinyScreen,
                                }"
                                class="flex shrink"
                            >
                                <v-img
                                    v-if="!tinyScreen"
                                    contain
                                    :height="96"
                                    :width="96"
                                    :class="{
                                        'shrink ':
                                            breakpoint.mdAndUp ||
                                            showFullDescription,
                                    }"
                                    src="/junior-tiny.webp"
                                    class="soli-round my-auto"
                                />
                                <div class="flex flex-col justify-center">
                                    <v-card-subtitle
                                        class="soli-grey !font-semibold py-0"
                                        >6 months - 5 years</v-card-subtitle
                                    >
                                    <v-card-title
                                        class="font-weight-bold accent--text pt-0 pb-0"
                                    >
                                        Musicologie Junior
                                    </v-card-title>
                                    <v-card-text
                                        v-if="
                                            breakpoint.smAndDown &&
                                            !showFullDescription
                                        "
                                        class="soli-grey py-0"
                                    >
                                        <span
                                            class="line-clamp-1"
                                            v-html="description"
                                        >
                                        </span>
                                        <v-btn
                                            text
                                            x-small
                                            rounded
                                            color="accent"
                                            outlined
                                            @click="
                                                showFullDescription =
                                                    !showFullDescription
                                            "
                                            >More
                                        </v-btn>
                                    </v-card-text>
                                </div>
                            </div>
                            <v-card-text
                                v-if="breakpoint.mdAndUp || showFullDescription"
                                class="soli-grey"
                            >
                                <span v-html="description" />
                            </v-card-text>
                            <div class="px-4">
                                <v-btn
                                    v-if="
                                        breakpoint.smAndDown &&
                                        showFullDescription
                                    "
                                    text
                                    x-small
                                    rounded
                                    outlined
                                    color="accent"
                                    @click="
                                        showFullDescription =
                                            !showFullDescription
                                    "
                                    >Less
                                </v-btn>
                            </div>
                        </div>
                        <div
                            class="flex flex-col desktop-scroll overflow-y-auto px-2"
                        >
                            <v-card-title
                                class="soli-grey pt-4 pb-2 px-1 font-weight-bold"
                                >Upcoming Classes</v-card-title
                            >
                            <div>
                                <v-skeleton-loader
                                    v-if="loading"
                                    class="w-full"
                                    type="list-item-avatar-three-line@6"
                                />
                                <div
                                    v-if="!loading && !classes.length"
                                    class="soli-grey px-1.5"
                                >
                                    Sorry! All of our classes are full. Contact
                                    us to join our waitlist!
                                </div>
                                <v-list
                                    v-if="classes.length && !loading"
                                    class="py-0"
                                >
                                    <v-list-item-group
                                        class="flex flex-col divide-y"
                                    >
                                        <div
                                            v-for="(item, idx) in classes"
                                            :key="idx"
                                        >
                                            <v-list-item
                                                class="!cursor-default !px-1.5 soli-round"
                                            >
                                                <v-list-item-content>
                                                    <v-list-item-title
                                                        class="font-semibold soli-grey"
                                                    >
                                                        {{
                                                            moment(
                                                                item.starts_at
                                                            ).format(
                                                                "ddd, MMM D"
                                                            )
                                                        }}
                                                    </v-list-item-title>
                                                    <v-list-item-subtitle>{{
                                                        `${moment(
                                                            item.starts_at
                                                        ).format(
                                                            "h:mm A"
                                                        )} - ${moment(
                                                            item.ends_at
                                                        ).format("h:mm A")}`
                                                    }}</v-list-item-subtitle>
                                                    <v-list-item-subtitle>{{
                                                        `Musicologie ${item.location.name}`
                                                    }}</v-list-item-subtitle>
                                                    <div
                                                        class="flex items-center"
                                                    >
                                                        <v-list-item-avatar
                                                            class="my-0 mr-2"
                                                            :size="28"
                                                        >
                                                            <img
                                                                :src="
                                                                    item.teacher
                                                                        .profile_photo_path
                                                                        ? `/api/image-service/40x40/${item.teacher.profile_photo_path}`
                                                                        : item
                                                                              .teacher
                                                                              .profile_photo_url
                                                                "
                                                            />
                                                        </v-list-item-avatar>

                                                        <v-list-item-subtitle>
                                                            {{
                                                                item.teacher
                                                                    .name
                                                            }}
                                                        </v-list-item-subtitle>
                                                    </div>
                                                </v-list-item-content>
                                                <v-list-item-action>
                                                    <div
                                                        class="flex flex-col gap-2"
                                                    >
                                                        <v-list-item-subtitle
                                                            class="py-1"
                                                            >{{
                                                                `${
                                                                    item.capacity -
                                                                    item.attending
                                                                } spot(s) left`
                                                            }}</v-list-item-subtitle
                                                        >
                                                        <v-btn
                                                            small
                                                            rounded
                                                            color="secondary"
                                                            class="shadow-orange"
                                                            :href="`/class-registration/${item.id}/edit`"
                                                            >Register</v-btn
                                                        >
                                                    </div>
                                                </v-list-item-action>
                                            </v-list-item>
                                        </div>
                                    </v-list-item-group>
                                </v-list>
                            </div>
                        </div>
                    </div>
                </v-container>
            </v-card>
        </v-col>
    </GetStartedLayout>
</template>

<style scoped>
.desktop-scroll {
    min-height: -webkit-fill-available;
    max-height: 45vh;
    max-height: 45svh;
}
</style>
